import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, HeartIcon } from './icons';
import type { WishlistItem } from '../types/app';

interface WishlistOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  items: WishlistItem[];
  isLoading: boolean;
  onRemoveItem: (wishProductId: number) => void;
  // SPEC-053: widened from (product: Product) to pass full WishlistItem with variant info
  onReserveItem?: (item: WishlistItem) => void;
  onProductClick?: (productId: number, variantId: number) => void;
  // Variant IDs already in cart — drives the Remove-from-bag button visibility.
  cartVariantIds?: Set<number>;
  // Handoff subtask #14: clicked when the variant is in cart — caller resolves the
  // basket product id from its own cart.items and calls cart.removeItem.
  onRemoveFromBag?: (item: WishlistItem) => void;
}

function WishlistItemRow({
  item,
  onRemoveItem,
  onReserveItem,
  onRemoveFromBag,
  onProductClick,
  isInCart,
}: {
  item: WishlistItem;
  onRemoveItem: (wishProductId: number) => void;
  // SPEC-053: widened to pass full item so caller can access item.productVariant.id
  onReserveItem?: (item: WishlistItem) => void;
  onRemoveFromBag?: (item: WishlistItem) => void;
  onProductClick?: (productId: number, variantId: number) => void;
  isInCart: boolean;
}) {
  const imageUrl = item.productVariant.image_url || item.product.image_url;

  return (
    <div className="flex items-center gap-4 py-4 border-b border-brand-gold/10 last:border-b-0">
      {/* Product thumbnail — clickable to open PDP */}
      <div
        className={`w-12 h-12 rounded-sm overflow-hidden flex-shrink-0 bg-brand-navy/5${onProductClick ? ' cursor-pointer' : ''}`}
        onClick={onProductClick ? () => onProductClick(item.product.id, item.productVariant.id) : undefined}
        role={onProductClick ? 'button' : undefined}
        tabIndex={onProductClick ? 0 : undefined}
        onKeyDown={onProductClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onProductClick(item.product.id, item.productVariant.id); } : undefined}
        aria-label={onProductClick ? `View details for ${item.product.title}` : undefined}
      >
        <img
          src={imageUrl}
          alt={item.product.title}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Product info */}
      <div className="flex-1 min-w-0">
        {/* Product title — clickable to open PDP */}
        <p
          className={`font-serif text-sm text-brand-navy truncate${onProductClick ? ' cursor-pointer hover:text-brand-gold transition-colors' : ''}`}
          onClick={onProductClick ? () => onProductClick(item.product.id, item.productVariant.id) : undefined}
          role={onProductClick ? 'button' : undefined}
          tabIndex={onProductClick ? 0 : undefined}
          onKeyDown={onProductClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onProductClick(item.product.id, item.productVariant.id); } : undefined}
        >
          {item.product.title}
        </p>
        <p className="text-xs text-brand-navy/60 truncate">
          {item.productVariant.title}
        </p>
        <p className="text-xs text-brand-navy font-medium mt-0.5">
          {item.productVariant.price}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        {onReserveItem && (
          isInCart ? (
            onRemoveFromBag ? (
              // Handoff subtask #14 — active Remove-from-bag button when variant is in cart.
              <button
                onClick={() => onRemoveFromBag(item)}
                className="px-2 py-1 text-xs font-medium text-brand-navy border border-brand-gold/30 rounded-sm hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors"
                aria-label={`Remove ${item.product.title} from bag`}
              >
                Remove from bag
              </button>
            ) : (
              // Fallback for callers that haven't wired onRemoveFromBag yet.
              <span
                className="px-2 py-1 text-xs font-medium text-brand-navy/50 border border-brand-gold/20 rounded-sm cursor-not-allowed"
                aria-label={`${item.product.title} is already in your cart`}
              >
                In Cart
              </span>
            )
          ) : (
            <button
              onClick={() => onReserveItem(item)}
              className="px-2 py-1 text-xs font-medium text-brand-navy border border-brand-gold/30 rounded-sm hover:bg-brand-gold/10 transition-colors"
              aria-label={`Reserve ${item.product.title}`}
            >
              Reserve
            </button>
          )
        )}
        {/* Remove button — filled HeartIcon */}
        <button
          onClick={() => onRemoveItem(item.id)}
          className="p-1.5 text-brand-gold hover:text-red-500 transition-colors"
          aria-label={`Remove ${item.product.title} from wishlist`}
        >
          <HeartIcon size={16} strokeWidth={1.5} fill="currentColor" />
        </button>
      </div>
    </div>
  );
}

export function WishlistOverlay({
  isOpen,
  onClose,
  items,
  isLoading,
  onRemoveItem,
  onReserveItem,
  onRemoveFromBag,
  onProductClick,
  cartVariantIds,
}: WishlistOverlayProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="absolute inset-10 bg-brand-cream/[0.98] backdrop-blur-sm flex flex-col shadow-2xl border border-brand-gold/20 z-20"
        >
          {/* Header */}
          <div className="px-8 py-6 border-b border-brand-gold/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="font-serif text-brand-navy tracking-wide text-lg">
                  Your Wishlist
                </h2>
                {items.length > 0 && (
                  <span className="text-xs text-brand-navy/60 bg-brand-navy/5 px-2 py-0.5 rounded-full">
                    {items.length} {items.length === 1 ? 'item' : 'items'}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-1 text-brand-navy/40 hover:text-brand-navy transition-colors"
                aria-label="Close wishlist"
              >
                <XIcon size={20} strokeWidth={1.5} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-4">
            {items.length === 0 ? (
              isLoading ? null : (
                /* Empty state — only after initial load resolves */
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <HeartIcon
                    size={48}
                    className="text-brand-navy/20"
                    strokeWidth={1}
                  />
                  <p className="font-serif text-brand-navy/60 text-sm">
                    Your wishlist is empty
                  </p>
                  <p className="text-xs text-brand-navy/40">
                    Items you love will appear here
                  </p>
                </div>
              )
            ) : (
              /* Items list */
              <div>
                {items.map((item) => (
                  <WishlistItemRow
                    key={item.id}
                    item={item}
                    onRemoveItem={onRemoveItem}
                    onReserveItem={onReserveItem}
                    onRemoveFromBag={onRemoveFromBag}
                    onProductClick={onProductClick}
                    isInCart={cartVariantIds?.has(item.productVariant.id) ?? false}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {!isLoading && items.length > 0 && (
            <div className="px-8 py-4 border-t border-brand-gold/20">
              <p className="text-sm text-brand-navy/60">
                {items.length} {items.length === 1 ? 'item' : 'items'} in your wishlist
              </p>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
